import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Card = styled.div`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 520px;
  margin: 2rem auto;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const LogoText = styled.span`
  font-size: 1.4rem;
  font-weight: 900;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  margin: 0 0 2rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const BankCard = styled.div`
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 14px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const BankName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #e2e8f0;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FieldRow = styled.div`
  margin-bottom: 1rem;
`;

const FieldLabel = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.35rem;
`;

const FieldValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FieldValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #e2e8f0;
  letter-spacing: 0.03em;
  flex: 1;
  word-break: break-all;
`;

const CopyBtn = styled.button<{ copied: boolean }>`
  background: ${p => p.copied ? 'rgba(16,185,129,0.15)' : '#2d2d5e'};
  color: ${p => p.copied ? '#10b981' : '#94a3b8'};
  border: 1px solid ${p => p.copied ? '#10b981' : '#4c4c8a'};
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: #3d3d7a; color: #e2e8f0; }
`;

const InfoBox = styled.div`
  background: rgba(124,58,237,0.08);
  border: 1px solid rgba(124,58,237,0.25);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: #a78bfa;
  line-height: 1.6;
`;

const ContactRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ContactBtn = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.85rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const WhatsAppBtn = styled(ContactBtn)`
  background: #25d366;
  color: white;
`;

const EmailBtn = styled(ContactBtn)`
  background: #2d2d5e;
  color: #e2e8f0;
  border: 1px solid #4c4c8a;
`;

const BankTransferPage: React.FC = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);

  const BANK_FIELDS = [
    { labelKey: 'transfer.field_titular', value: 'FotoVibe', copyable: false },
    { labelKey: 'transfer.field_banco', value: 'BBVA México', copyable: false },
    { labelKey: 'transfer.field_cuenta', value: '1516770960', copyable: true },
    { labelKey: 'transfer.field_clabe', value: '012180015167709600', copyable: true },
    { labelKey: 'transfer.field_swift', value: 'BCMRMXMMPYM', copyable: true },
  ];

  const handleCopy = (value: string, labelKey: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(labelKey);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const whatsappMsg = encodeURIComponent(t('transfer.whatsapp_msg'));
  const whatsappUrl = `https://wa.me/525573577550?text=${whatsappMsg}`;

  return (
    <Wrapper>
      <Card>
        <LogoRow>
          <LogoText>FotoVibe</LogoText>
        </LogoRow>

        <Title>{t('transfer.title')}</Title>
        <Subtitle>{t('transfer.sub')}</Subtitle>

        <BankCard>
          <BankName>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
            {t('transfer.bank_title')}
          </BankName>

          {BANK_FIELDS.map(f => (
            <FieldRow key={f.labelKey}>
              <FieldLabel>{t(f.labelKey)}</FieldLabel>
              <FieldValueRow>
                <FieldValue>{f.value}</FieldValue>
                {f.copyable && (
                  <CopyBtn type="button" copied={copied === f.labelKey} onClick={() => handleCopy(f.value, f.labelKey)}>
                    {copied === f.labelKey ? t('transfer.copied') : t('transfer.copy')}
                  </CopyBtn>
                )}
              </FieldValueRow>
            </FieldRow>
          ))}
        </BankCard>

        <InfoBox>
          <strong>{t('transfer.instructions_title')}</strong>
          <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', lineHeight: '1.8' }}>
            <li>{t('transfer.step1')}</li>
            <li>{t('transfer.step2')}</li>
            <li>{t('transfer.step3')}</li>
            <li>{t('transfer.step4')}</li>
          </ol>
        </InfoBox>

        <ContactRow>
          <WhatsAppBtn href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </WhatsAppBtn>
          <EmailBtn href="mailto:dynappmos@gmail.com?subject=Pago FotoVibe&body=Hola, acabo de realizar una transferencia para el plan de FotoVibe. Adjunto el comprobante.">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {t('transfer.email_btn')}
          </EmailBtn>
        </ContactRow>
      </Card>
    </Wrapper>
  );
};

export default BankTransferPage;
